import mapboxgl from 'mapbox-gl'
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder'
import MapboxDraw from '@mapbox/mapbox-gl-draw'
import turf from '@turf/turf'
import queryString from 'query-string'
import axios from 'axios'
import pinImg from '/assets/images/pin.png'
import pinActiveImg from '/assets/images/pin-active.png'
import pinIconImg from '/assets/images/pin-icon.png'
import pinIconActiveImg from '/assets/images/pin-icon-active.png'
import pinPriorityImg from '/assets/images/pin-priority.png'
import starImg from '/assets/images/star.png'
import heartImg from '/assets/images/heart.png'
import clusterBadgePriority from '/assets/images/cluster-badge-priority.png'
import { debounce } from './utils'
import $ from 'domtastic'

let geojson = null
let activePointId = null
let popupIndex = 0
let popupIds = []
const popup = new mapboxgl.Popup()

class RentlerMap {
  constructor(element) {
    this.element = element
    this.skipMoveEnd = false
    this.skipBoundsUpdate = false

    this.initializeMap()

    this.updateMapMarkers = this.updateMapMarkers.bind(this)
  }

  initializeMap() {
    const initialBounds = document
      .querySelector('#map')
      .getAttribute('data-initial-bounds')
      .replace('[', '')
      .replace(']', '')
      .split(',')
      .map((val) => parseFloat(val))

    const formatGeocoderResult = (json) => {
      let result = json.place_name
      const country = json.context.find((x) => x.id.startsWith('country'))
      if (country) {
        result = result.replace(`, ${country.text}`, '')
      }
      return result
    }

    mapboxgl.accessToken = rentler.listings.mapboxToken
    var map = new mapboxgl.Map({
      container: 'map',
      style: 'mapbox://styles/rentler/ck2z9y0ey0ge61cs3v1ur3yvo',
      fadeDuration: 0,
      ...(initialBounds.length
        ? {
            bounds: initialBounds,
          }
        : { center: [-103.59179687498357, 40.66995747013945] }),
      zoom: 8,
      minZoom: 1,
      maxZoom: 22
    })
    const geocoder = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken,
      mapboxgl: mapboxgl,
      flyTo: false,
      marker: false,
      countries: 'us',
      types: 'neighborhood,locality,place,district,postcode,region,country',
      proximity: { latitude: 40.7665628, longitude: -111.9633482 },
      getItemValue: (json) => formatGeocoderResult(json),
      render: (json) => {
        const title = formatGeocoderResult(json)
        return `
        <div class="mapboxgl-ctrl-geocoder--suggestion">
          <div class="mapboxgl-ctrl-geocoder--suggestion-title">${title}</div>
        </div>
        `
      },
      clearOnBlur: true,
    })
    var draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: {
        polygon: true,
        trash: true,
      },
      styles: [
        // ACTIVE (being drawn)
        // line stroke
        {
          id: 'gl-draw-line',
          type: 'line',
          filter: [
            'all',
            ['==', '$type', 'LineString'],
            ['!=', 'mode', 'static'],
          ],
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
          paint: {
            'line-color': '#5F9298',
            // "line-dasharray": [0.2, 2],
            'line-width': 2,
          },
        },
        // polygon fill
        {
          id: 'gl-draw-polygon-fill',
          type: 'fill',
          filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
          paint: {
            'fill-color': '#5F9298',
            'fill-outline-color': '#5F9298',
            'fill-opacity': 0.1,
          },
        },
        // polygon outline stroke
        // This doesn't style the first edge of the polygon, which uses the line stroke styling instead
        {
          id: 'gl-draw-polygon-stroke-active',
          type: 'line',
          filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
          paint: {
            'line-color': '#5F9298',
            // "line-dasharray": [1, 1],
            'line-width': 2,
          },
        },
        // vertex point halos
        {
          id: 'gl-draw-polygon-and-line-vertex-halo-active',
          type: 'circle',
          filter: [
            'all',
            ['==', 'meta', 'vertex'],
            ['==', '$type', 'Point'],
            ['!=', 'mode', 'static'],
          ],
          paint: {
            'circle-radius': 5,
            'circle-color': '#FFF',
          },
        },
        // vertex points
        {
          id: 'gl-draw-polygon-and-line-vertex-active',
          type: 'circle',
          filter: [
            'all',
            ['==', 'meta', 'vertex'],
            ['==', '$type', 'Point'],
            ['!=', 'mode', 'static'],
          ],
          paint: {
            'circle-radius': 3,
            'circle-color': '#5F9298',
          },
        },

        // INACTIVE (static, already drawn)
        // line stroke
        {
          id: 'gl-draw-line-static',
          type: 'line',
          filter: [
            'all',
            ['==', '$type', 'LineString'],
            ['==', 'mode', 'static'],
          ],
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
          paint: {
            'line-color': '#000',
            'line-width': 3,
          },
        },
        // polygon fill
        {
          id: 'gl-draw-polygon-fill-static',
          type: 'fill',
          filter: ['all', ['==', '$type', 'Polygon'], ['==', 'mode', 'static']],
          paint: {
            'fill-color': '#000',
            'fill-outline-color': '#000',
            'fill-opacity': 0.1,
          },
        },
        // polygon outline
        {
          id: 'gl-draw-polygon-stroke-static',
          type: 'line',
          filter: ['all', ['==', '$type', 'Polygon'], ['==', 'mode', 'static']],
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
          paint: {
            'line-color': '#000',
            'line-width': 3,
          },
        },
      ],
    })

    // If touch device disable draw feature
    if (!('ontouchstart' in window)) {
      // map.addControl(draw)
      map.addControl(new mapboxgl.NavigationControl())
    }

    map.on('draw.create', (e) => {
      let data = draw.getAll()
      if (data.features.length > 0) {
        document.querySelector('.mapbox-gl-draw_polygon').classList.add('hide')
        document.querySelector('.mapbox-gl-draw_trash').classList.add('show')
      }
    })
    map.on('draw.delete', (e) => {
      document.querySelector('.mapbox-gl-draw_polygon').classList.remove('hide')
      document.querySelector('.mapbox-gl-draw_trash').classList.remove('show')
    })
    map.on('draw.update', (e) => {
      let data = draw.getAll()
      if (data.features.length > 0) {
        // console.log('data', data)
      }
    })

    const that = this

    // Search Box
    document.getElementById('geocoder').appendChild(geocoder.onAdd(map))

    const location = document
      .getElementById('geocoder')
      .getAttribute('data-location')

    if (location) {
      this.setLocation(location)
    }

    geocoder.on('result', (e) => {
      const location = formatGeocoderResult(e.result)

      window.FilterUtility.filterValues.savedsearches = false
      window.FilterUtility.filterValues.location = location

      // Get geo for location
      axios
        .post(`${rentler.apiBaseUri}/locations`, { location })
        // If we successfully get a geo fit map bounds to geo bounds
        .then((response) => {
          const { ne, sw } = response.data.bounds
          const bounds = [ne.lon, ne.lat, sw.lon, sw.lat]
          window.FilterUtility.filterValues.bounds = bounds
          this.fitBounds(bounds)
          window.FilterUtility.searchFilters(true)
        })
        // Otherwise fallback and fit map bounds to geocoder bounds
        .catch(() => {
          const { bbox } = e.result
          window.FilterUtility.filterValues.bounds = bbox
          this.fitBounds(bbox)
          window.FilterUtility.searchFilters(true)
        })

      if (window.FilterUtility.filterValues.bounds)
        window.FilterUtility.filterValues.bounds.length = 0
      document.querySelector('.filter-input input').blur()
    })

    document.querySelector('.filter-input input').onfocus = () => {
      document.querySelector('.filter-input .btn-primary').classList.add('open')
      document
        .querySelector('.filter-input .mapboxgl-ctrl-geocoder.mapboxgl-ctrl')
        .classList.add('focus')
    }
    document.querySelector('.filter-input input').onblur = () => {
      document
        .querySelector('.filter-input .btn-primary')
        .classList.remove('open')
      document
        .querySelector('.filter-input .mapboxgl-ctrl-geocoder.mapboxgl-ctrl')
        .classList.remove('focus')
    }

    const $geocoder = $('.mapboxgl-ctrl-geocoder')
    const $geocoderInput = $('.mapboxgl-ctrl-geocoder--input')
    $geocoderInput.on('change keyup focus', function () {
      if ($geocoderInput.val().length > 0) {
        $geocoder.addClass('has-value')
      } else {
        $geocoder.removeClass('has-value')
      }
    })

    this.rentlerMap = map
    this.geocoder = geocoder

    window.rentlerMap = map

    map.on('load', function () {
      const isMyPlaces = window.FilterUtility.filterValues.myplaces
      // Add a new source from our GeoJSON data and
      // set the 'cluster' option to true. GL-JS will
      // add the point_count property to your source data.

      const params = window.FilterUtility.getParams()
      delete params.location

      params.bounds = map.getBounds().toArray().flat()
      const qs = queryString.stringify(params, { arrayFormat: 'none' })
      const searchApi = isMyPlaces
        ? `${rentler.apiBaseUri}/favorites/searchmap`
        : `${rentler.apiBaseUri}/searchmap`

      axios.get(`${searchApi}?${qs}`).then((response) => {
        geojson = response.data

        const favorites = $('[data-favorites]')
          .attr('data-favorites')
          .split(',')
        geojson.features.forEach((f) => {
          f.active = false
          f.properties.liked = favorites.includes(`${f.properties.id}`)
        })

        map.addSource('rents', {
          type: 'geojson',
          data: geojson,
          cluster: true,
          maxzoom: 22,
          clusterMaxZoom: 22, // Max zoom to cluster points on
          clusterRadius: 30, // Radius of each cluster when clustering points (defaults to 50)
          clusterProperties: {
            total_count: ['+', ['case', ['has', 'count'], ['get', 'count'], 1]],
            any_priority: ['any', ['==', ['get', 'priority'], true], 'false'],
          },
        })

        const imgs = [
          { id: 'cluster_badge_priority', src: clusterBadgePriority },
          { id: 'pin', src: pinImg },
          { id: 'pin_active', src: pinActiveImg },
          { id: 'pin_icon', src: pinIconImg },
          { id: 'pin_icon_active', src: pinIconActiveImg },
          { id: 'pin_priority', src: pinPriorityImg },
          { id: 'heart', src: heartImg },
          { id: 'star', src: starImg },
        ]

        // Load all images before adding layers so that layers can be added in order
        Promise.all(
          imgs.map(
            (item) =>
              new Promise((resolve, _reject) => {
                map.loadImage(
                  `${rentler.baseUri}/dist/${item.src}`,
                  (err, image) => {
                    if (err) throw err
                    map.addImage(item.id, image)
                    resolve()
                  }
                )
              })
          )
        ).then(() => {
          // Clusters layer
          map.addLayer({
            id: 'clusters',
            type: 'circle',
            source: 'rents',
            interactive: true,
            filter: ['has', 'point_count'],
            paint: {
              'circle-color': '#DA291C',
              // Use step expressions (https://docs.mapbox.com/mapbox-gl-js/style-spec/#expressions-step)
              'circle-radius': [
                'step',
                ['get', 'total_count'],
                12,
                10,
                17,
                20,
                19.5,
                30,
                24.5,
                40,
                29.5,
                100,
                34.5,
                300,
                39.5,
              ],
              'circle-stroke-width': 3,
              'circle-stroke-color': '#fff',
            },
          })

          // Cluster count layer
          map.addLayer({
            id: 'cluster-count',
            type: 'symbol',
            source: 'rents',
            filter: ['has', 'point_count'],
            layout: {
              'text-font': ['Poppins SemiBold'],
              'text-field': [
                'case',

                // Million abbreviation
                ['all', ['>=', ['abs', ['get', 'total_count']], 1e6]],
                [
                  'concat',
                  [
                    'to-string',
                    [
                      'number-format',
                      ['/', ['get', 'total_count'], 1e6],
                      { 'min-fraction-digits': 1, 'max-fraction-digits': 1 },
                    ],
                  ],
                  'M',
                ],

                // Thousand abbreviation
                ['all', ['>=', ['abs', ['get', 'total_count']], 1e3]],
                [
                  'concat',
                  [
                    'to-string',
                    [
                      'number-format',
                      ['/', ['get', 'total_count'], 1e3],
                      { 'min-fraction-digits': 1, 'max-fraction-digits': 1 },
                    ],
                  ],
                  'k',
                ],

                // Default (no abbreviation)
                ['get', 'total_count'],
              ],
              'text-size': 14,
              'text-allow-overlap': true,
            },
            paint: {
              'text-color': '#ffffff',
            },
          })

          // Cluster priority badge layer
          map.addLayer({
            id: 'cluster-badge-priority',
            type: 'symbol',
            source: 'rents',
            filter: [
              'all',
              ['has', 'point_count'],
              ['==', 'any_priority', true],
            ],
            layout: {
              'icon-image': 'cluster_badge_priority',
              'icon-offset': [
                'case',
                ['<=', ['get', 'total_count'], 10],
                ['literal', [15, -10]],
                ['<=', ['get', 'total_count'], 20],
                ['literal', [18, -12]],

                ['<=', ['get', 'total_count'], 30],
                ['literal', [17, -14]],
                ['<=', ['get', 'total_count'], 40],
                ['literal', [20, -16]],

                ['<=', ['get', 'total_count'], 100],
                ['literal', [24, -20]],
                ['<=', ['get', 'total_count'], 300],
                ['literal', [28, -24]],

                ['<=', ['get', 'total_count'], 500],
                ['literal', [32, -26]],
                ['<=', ['get', 'total_count'], 1000],
                ['literal', [34, -28]],

                ['literal', [14, -10]],
              ],
              'icon-size': 1.0,
              'icon-allow-overlap': true,
            },
          })

          // Unclustered point layer
          map.addLayer({
            id: 'unclustered-point',
            interactive: true,
            type: 'symbol',
            source: 'rents',
            filter: ['!', ['has', 'point_count']],
            layout: {
              'text-field': ['get', 'price'],
              'text-font': ['Poppins Bold'],
              'text-size': 14,
              'text-allow-overlap': true,
              'text-offset': [
                'case',

                // Liked
                ['==', ['get', 'liked'], true],
                ['literal', [0.59, -0.375]],

                // Priority
                ['==', ['get', 'priority'], true],
                ['literal', [0.59, -0.375]],

                // Default
                ['literal', [-0.08, -0.375]],
              ],
              'icon-image': [
                'case',

                // Active w/ icon
                [
                  'all',
                  ['==', ['get', 'active'], true],
                  [
                    'any',
                    ['==', ['get', 'priority'], true],
                    ['==', ['get', 'liked'], true],
                  ],
                ],
                'pin_icon_active',

                // Active no icon
                ['==', ['get', 'active'], true],
                'pin_active',

                // Priority
                ['==', ['get', 'priority'], true],
                'pin_priority',

                // Liked
                ['==', ['get', 'liked'], true],
                'pin_icon',

                // Default
                'pin',
              ],
              'icon-size': 1,
              'icon-allow-overlap': true,
            },
            paint: {
              'text-color': '#fff',
            },
          })

          // Unclustered point icon layer
          map.addLayer({
            id: 'unclustered-point-icon',
            interactive: false,
            type: 'symbol',
            source: 'rents',
            filter: [
              'all',
              ['!', ['has', 'point_count']],
              [
                'any',
                ['==', ['get', 'priority'], true],
                ['==', ['get', 'liked'], true],
              ],
            ],
            layout: {
              'icon-image': [
                'case',

                // Liked
                ['==', ['get', 'liked'], true],
                'heart',

                // Priority
                ['==', ['get', 'priority'], true],
                'star',

                // Default
                '',
              ],
              'icon-offset': [
                'case',

                // Liked
                ['==', ['get', 'liked'], true],
                ['literal', [-26, -4.75]],

                // Priority
                ['==', ['get', 'priority'], true],
                ['literal', [-26, -5.75]],

                // Default
                ['literal', [0, 0]],
              ],
              'icon-size': 1,
              'icon-allow-overlap': true,
            },
          })
        })

        if (isMyPlaces) that.fitMapMarkerBounds(response.data, { duration: 0 })
      })

      map.on(
        'moveend',
        debounce(function (e) {
          if (that.skipMoveEnd) {
            that.skipMoveEnd = false
            return
          }
          // todo:
          // set bounds
          window.FilterUtility.filterValues.bounds = map
            .getBounds()
            .toArray()
            .flat()
          // trigger search
          // window.FilterUtility.filterValues.bounds
          that.onUpdateMapLocation(map.getBounds())
        }, 250)
      )

      // Inspect a cluster on click
      map.on('click', 'clusters', function (e) {
        const features = map.queryRenderedFeatures(e.point, {
          layers: ['clusters'],
        })
        const clusterId = features[0].properties.cluster_id
        map
          .getSource('rents')
          .getClusterExpansionZoom(clusterId, function (err, zoom) {
            if (err) return

            map.easeTo({
              center: features[0].geometry.coordinates,
              zoom: zoom,
            })
          })
      })

      // Use pointer cusror on hover for interactive layers
      const interactiveLayers = ['clusters', 'unclustered-point']
      interactiveLayers.forEach((layer) => {
        map
          .on(
            'mouseenter',
            layer,
            () => (map.getCanvas().style.cursor = 'pointer')
          )
          .on('mouseleave', layer, () => (map.getCanvas().style.cursor = ''))
      })

      map.on('click', 'unclustered-point', (e) => {
        const {
          geometry,
          properties: { id, ids },
        } = e.features[0]

        // Toggle clicked point active state
        const point = geojson.features.find((f) => f.properties.id === id)
        point.properties.active = !point.properties.active

        // Apply new active state
        map.getSource('rents').setData(geojson)

        // Update active point
        activePointId = point.properties.active ? id : null

        if (!point.properties.active) return

        // Popup
        axios.get(`${rentler.apiBaseUri}/listings/${id}`).then((response) => {
          const listing = response.data

          // Init popup vars
          popupIndex = 0
          popupIds = ids ? ids.split(',') : []

          // Ensure that if the map is zoomed out such that multiple
          // copies of the feature are visible, the popup appears
          // over the copy being pointed to.
          const coordinates = geometry.coordinates.slice()
          while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360
          }

          // Build html and show popup
          const html = buildPopupHtml(listing)
          popup.setLngLat(coordinates).setHTML(html).addTo(map)
        })
      })

      popup.on('close', () => {
        if (!activePointId) return

        // Remove active state from points on close
        let points = geojson.features.filter((f) => f.properties.active)
        if (points.length > 1) {
          points = points.filter((f) => f.properties.id !== activePointId)
        }
        points.forEach((f) => (f.properties.active = false))

        map.getSource('rents').setData(geojson)
      })

      $('body').on('click', '.listing-item .pager-btn', (e) => {
        e.preventDefault()

        // Get popup index
        const $btn = $(e.target)
        const isNext = $btn.hasClass('next')
        popupIndex += isNext ? 1 : -1

        // Get listing
        const listingId = popupIds[popupIndex]
        axios
          .get(`${rentler.apiBaseUri}/listings/${listingId}`)
          .then((response) => {
            const listing = response.data

            // Build html and show popup
            const html = buildPopupHtml(listing)
            popup.setHTML(html)
          })
      })
    })
  }

  onUpdateMapLocation(bounds) {
    let activeQuery = queryString.parseUrl(window.location.href, {
      arrayFormat: 'none',
    }).query
    activeQuery.bounds = bounds.toArray().flat()
    window.mapActiveBounds = bounds

    if (this.skipBoundsUpdate) {
      this.skipBoundsUpdate = false
    } else {
      this.clearLocation()
      window.FilterUtility.filterValues.location = ''
      window.FilterUtility.filterValues.bounds = activeQuery.bounds
    }

    if (window.FilterUtility) {
      const isMapSearch = true
      window.FilterUtility.searchFilters(isMapSearch)
    }

    if (window.popup) window.popup._closeButton.click()
  }

  updateMapMarkers(source) {
    geojson = source
    this.rentlerMap.getSource('rents').setData(geojson)
  }

  fitMapMarkerBounds(source, opts = {}) {
    this.skipMoveEnd = true
    const bounds = new mapboxgl.LngLatBounds()
    source.features.forEach((f) => bounds.extend(f.geometry.coordinates))
    this.rentlerMap.fitBounds(bounds, { duration: 750, ...opts })
  }

  fitBounds(bounds, opts = {}) {
    this.skipMoveEnd = true
    this.rentlerMap.fitBounds(bounds, { duration: 750, ...opts })
  }

  setLocation(value) {
    document.querySelector('#geocoder .mapboxgl-ctrl-geocoder--input').value =
      value
  }

  getLocation() {
    return document.querySelector('#geocoder .mapboxgl-ctrl-geocoder--input')
      .value
  }

  clearLocation() {
    // NOTE:
    // We are intentionally not calling the `clear()` function on the geocoder
    // because it causes an issue where the keyboard open and closes multiple times
    // on android.
    this.geocoder.setInput('')
  }
}

const buildPopupHtml = (listing) => {
  const {
    listingId,
    ribbonColor,
    ribbonText,
    street,
    thumbnailUrls,
    uiBedrooms,
    uiBathrooms,
    uiCityState,
    uiPrice,
    uiPropertyType,
    url,
  } = listing

  const liked = $('[data-favorites]')
    .attr('data-favorites')
    .split(',')
    .includes(listingId.toString())

  const html = `
    <div class="listing-item${liked ? ' liked' : ''}${
    popupIds.length ? ' has-pager' : ''
  }" data-id="${listingId}" data-liked="${liked}">
      <a href="${url}">
        ${
          ribbonText
            ? `<span class="tag" style="background-color: ${ribbonColor}">${ribbonText}</span>`
            : ''
        }
        <div class="img-wrapper">
        ${
          thumbnailUrls.length
            ? `<div class="image" style="background-image: url('${thumbnailUrls[0]}')"></div>`
            : '<div class="image no-image"></div>'
        }
      </div>
      </a>
        <a href="#" class="btn-favorite">
          <div class="icon">
            <span class="icon-heart-outline"></span>
            <span class="icon-heart-fill"></span>
          </div>
        </a>
        <a href="${url}">
          <div class="text">
            <h3 class="price">${uiPrice}</h3>
            <div class="address">${street}<br /> ${uiCityState}</div>
            <div class="bed-bath">
              ${uiBedrooms} &#149; ${uiBathrooms}<br />
              ${uiPropertyType}
            </div>
          </div>
        </a>
        ${
          popupIds.length
            ? `<div class="pager">
                <a href="#" class="pager-btn prev${
                  popupIndex <= 0 ? ' disabled' : ''
                }">
                  <span class="icon-left"></span>
                </a>
                <div class="pager-desc">${popupIndex + 1} / ${
                popupIds.length
              }</div>
                <a href="#" class="pager-btn next${
                  popupIndex >= popupIds.length - 1 ? ' disabled' : ''
                }">
                  <span class="icon-right"></span>
                </a>
              </div>

              `
            : ''
        }
      </div>
    `

  return html
}

export default RentlerMap
